import { TerciarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import Toggle from 'components/ReusableComponents/Toggle'
import ConfigureWidgets, {
  getWidgetProperties,
  widgetNames
} from 'features/ConfigureWidgets'
import WidgetCampaignCard from 'features/Widgets/CampaignCard'
import EditWidgetPromoDialog, {
  EditWidgetPromoWithoutDialog
} from 'features/Widgets/EditWidgetPromoDialog'
import ExcludedPagesDialog from 'features/Widgets/ExcludedPagesDialog'
import WidgetsCard from 'features/Widgets/WidgetsCard'
import NoEmailSubscriptionWarning from 'features/Widgets/NoEmailSubscriptionWarning'
import WidgetSnippet from 'features/Widgets/WidgetSnippet'
import { useInternalAdmin } from 'hooks'
import useBrand from 'hooks/useBrand'
import { directfulServiceTypes } from 'main/directfulServices'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import widgetsApi from 'services/api/widgets'

import styles from './Widgets.module.scss'

const { thisWeek, last7days, last14days, last30days, minus1Month } = smartDateTypes

const Widgets = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [data, setData] = useState([])
  const [widgets, setWidgets] = useState([])

  const [fromDate, setFromDate] = useState(moment().subtract(1, 'month'))
  const [toDate, setToDate] = useState(moment())

  const [isIntegrated, setIsIntegrated] = useState(null)
  const [isOnline, setIsOnline] = useState(null)

  const [selectedDeviceData, setSelectedDeviceData] = useState([])

  const [devices, setDevices] = useState('both')

  const [widgetKindBeingEdited, setWidgetKindBeingEdited] = useState()

  const [isCheckingStatus, setIsCheckingStatus] = useState(false)

  const [promotion, setPromotion] = useState({})

  const [siteMap, setSiteMap] = useState([])

  const [isSavingPromotion, setIsSavingPromotion] = useState(false)

  const [isPromotionEditDialogVisible, setIsPromotionEditDialogVisible] = useState(false)

  const [isSnippetDialogVisible, setIsSnippetDialogVisible] = useState(false)

  const [isExclusionDialogVisible, setIsExclusionDialogVisible] = useState(false)

  const [isPopupDialogVisible, setIsPopupDialogVisible] = useState(false)

  const [hotelName, setHotelName] = useState('')

  const [textMarketingUrlKey, setTextMarketingUrlKey] = useState('')

  const { addToast } = useToasts()

  const isInternalAdmin = useInternalAdmin()

  const activeServices = useSelector(s => s.hotelDetails.activeServices)
  const hasEmailService = activeServices?.find(s => s.plan === directfulServiceTypes.Email)

  const { colors: brandColors, logoUrl: brandLogoUrl } = useBrand()
  const hasFilledDefaults = useRef(false)

  function handleSavePromotion({
    contentId,
    promotionCode,
    minNightsToStay,
    maxDiscountPercentage,
    promotionTagline
  }) {
    setIsSavingPromotion(true)

    widgetsApi
      .updatePromotion({
        contentId,
        promotionCode,
        minNightsToStay,
        maxDiscountPercentage,
        promotionTagline
      })
      .then(res => {
        setPromotion(res)
        addToast('Promotion is updated', { appearance: 'success' })
      })
      .finally(() => {
        setIsSavingPromotion(false)
        setIsPromotionEditDialogVisible(false)
      })
  }

  function checkWidgetStatus() {
    setIsCheckingStatus(true)
    widgetsApi
      .checkStatus()
      .then(res => {
        setIsOnline(res.isOnline)
        setIsIntegrated(res.isIntegrated)
      })
      .catch(() => {
        setIsOnline(false)
      })
      .finally(() => {
        setIsCheckingStatus(false)
      })
  }

  function toggleWidget(isActive, kind, widgetId) {
    widgetsApi.list
      .post({
        data: [
          {
            isActive,
            kind,
            widgetId
          }
        ]
      })
      .then(() => {
        widgetsApi
          .analytics({ fromDate: fromDate.toDate(), toDate: toDate.toDate() })
          .then(response => {
            setData(response.facts)
            setWidgets(response.widgets || [])
          })
      })
  }

  useEffect(() => {
    widgetsApi
      .analytics({ fromDate: fromDate.toDate(), toDate: toDate.toDate() })
      .then(response => {
        const widgetsNeedDefaults =
          !hasFilledDefaults.current &&
          response.widgets?.some(
            w => !w.data || w.data === '{}' || !JSON.parse(w.data)?.popupTitle
          )

        if (widgetsNeedDefaults) {
          hasFilledDefaults.current = true
          const defaults = getWidgetProperties(brandColors)
          const filledWidgets = response.widgets.map(w => {
            const parsed = w.data ? JSON.parse(w.data) : {}
            const base = { widgetId: w.id, kind: w.kind, isActive: w.isActive }
            if (!parsed.popupTitle) {
              return {
                ...base,
                data: JSON.stringify({
                  ...defaults[w.kind],
                  brandColor: brandColors.action,
                  logoUrl: brandLogoUrl,
                  hotelName: response.hotelName,
                  textMarketingUrlKey: response.textMarketingUrlKey,
                  ...parsed
                })
              }
            }
            return { ...base, data: w.data }
          })

          return widgetsApi.list.post({ data: filledWidgets }).then(() =>
            widgetsApi.analytics({
              fromDate: fromDate.toDate(),
              toDate: toDate.toDate()
            })
          )
        }

        return response
      })
      .then(response => {
        setData(response.facts)
        setWidgets(response.widgets || [])
        setIsIntegrated(response.isIntegrated)
        setPromotion(response.promotion)
        setIsOnline(response.isOnline)
        setSiteMap(response.sitemaps)
        setHotelName(response?.hotelName)
        setTextMarketingUrlKey(response?.textMarketingUrlKey)
      })
      .catch(error => {
        console.warn(error)
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [fromDate, toDate])

  useEffect(() => {
    if (!widgets?.length) {
      setSelectedDeviceData([])
      return
    }

    const deviceKindFilter =
      devices === 'desktop' ? 'Desktop' : devices === 'mobile' ? 'Mobile' : null

    setSelectedDeviceData(
      widgets.map(widget => {
        const matchingFacts = data.filter(f => f.widgetId === widget.id)
        const filteredFacts = deviceKindFilter
          ? matchingFacts.filter(f => f.deviceKind === deviceKindFilter)
          : matchingFacts

        return {
          ...widget,
          totalImpressions: filteredFacts.reduce(
            (sum, f) => sum + (f.totalImpressions || 0),
            0
          ),
          totalEngagement: filteredFacts.reduce(
            (sum, f) => sum + (f.totalEngagement || 0),
            0
          ),
          totalConversions: filteredFacts.reduce(
            (sum, f) => sum + (f.totalConversions || 0),
            0
          )
        }
      })
    )
  }, [devices, data, widgets])

  if (isErred) {
    return <div>Something went wrong</div>
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Site abandonment" width={900} />
      {!hasEmailService && <NoEmailSubscriptionWarning />}
      {isPromotionEditDialogVisible && (
        <EditWidgetPromoDialog
          {...promotion}
          onSave={handleSavePromotion}
          isSaving={isSavingPromotion}
          onClose={() => setIsPromotionEditDialogVisible(false)}
        />
      )}
      {isLoading && (
        <div style={{ marginBottom: 60, padding: 20 }} data-testid="loading">
          <LoadingIndicator />
        </div>
      )}
      {!isLoading && !isIntegrated && (
        <div style={{ marginBottom: 60 }}>
          <WidgetSnippet
            hasWrapper={false}
            isOnline={isOnline}
            onCheckClick={checkWidgetStatus}
            isChecking={isCheckingStatus}
          />
        </div>
      )}
      {!promotion?.promotionCode && isIntegrated && (
        <div className={styles.promotionHolder}>
          <EditWidgetPromoWithoutDialog
            {...promotion}
            onSubmit={handleSavePromotion}
            isSaving={isSavingPromotion}
          />
        </div>
      )}
      {widgetKindBeingEdited && (
        <ConfigureWidgets
          widgetKind={widgetKindBeingEdited}
          hotelName={hotelName}
          textMarketingUrlKey={textMarketingUrlKey}
          onClose={() => setWidgetKindBeingEdited(null)}
        />
      )}
      <div
        style={{
          opacity: isLoading || (isIntegrated && promotion?.promotionCode) ? 1 : 0.4,
          pointerEvents: isLoading || !(isIntegrated && promotion?.promotionCode) ? 'none' : 'auto'
        }}
      >
        <div className={styles.titleHolder}>
          <p className={styles.title}>Widgets</p>
          {!isLoading && isIntegrated && promotion?.promotionCode && (
            <div className={styles.filters}>
              <Dropdown
                value={devices}
                className={styles.devicesDropdown}
                onChange={e => setDevices(e.target.value)}
                options={{
                  both: 'Desktop & mobile',
                  desktop: 'Desktop',
                  mobile: 'Mobile'
                }}
              />
              <SmartDatepicker
                startDate={fromDate}
                endDate={toDate}
                onChangeStartDate={setFromDate}
                onChangeEndDate={setToDate}
                smartDates={[thisWeek, last7days, last14days, last30days, minus1Month]}
              />
            </div>
          )}
        </div>
        <Table widths={[50, 400, 100, 100, 120]}>
          <TitleRow>
            <TitleCell></TitleCell>
            <TitleCell>Widget type</TitleCell>
            <TitleCell>Impressions</TitleCell>
            <TitleCell>Engagement</TitleCell>
            <TitleCell>Conversions</TitleCell>
            <TitleCell></TitleCell>
          </TitleRow>
          <div className={styles.rows} data-testid={isLoading ? 'table-skeleton' : undefined}>
            {isLoading ? (
              <>
                {[1, 2, 3, 4].map(i => (
                  <Row key={i} className={styles.row}>
                    <Cell className={styles.statusCell}>
                      <span className={styles.skeletonCell} style={{ width: 36, height: 20 }} />
                    </Cell>
                    <Cell className={styles.widgetTypeCell}>
                      <span className={styles.skeletonCell} style={{ width: '80%', maxWidth: 120 }} />
                    </Cell>
                    <Cell className={styles.dataCell}>
                      <span className={styles.skeletonCell} style={{ width: 40 }} />
                    </Cell>
                    <Cell className={styles.dataCell}>
                      <span className={styles.skeletonCell} style={{ width: 40 }} />
                    </Cell>
                    <Cell className={styles.dataCell}>
                      <span className={styles.skeletonCell} style={{ width: 40 }} />
                    </Cell>
                    <Cell className={styles.actionsCell} />
                  </Row>
                ))}
              </>
            ) : (
              selectedDeviceData.map((row, index) => (
                <Row key={index} className={styles.row}>
                  <Cell className={styles.statusCell}>
                    <Toggle
                      checked={row.isActive}
                      disabled={!(isIntegrated && promotion?.promotionCode)}
                      handleChange={e => toggleWidget(e.target.checked, row.kind, row.id)}
                    />
                  </Cell>
                  <Cell className={styles.widgetTypeCell}>{widgetNames[row.kind]}</Cell>
                  <Cell className={styles.dataCell}>{row.totalImpressions}</Cell>
                  <Cell className={styles.dataCell}>{row.totalEngagement}</Cell>
                  <Cell className={styles.dataCell}>{row.totalConversions}</Cell>
                  <Cell className={styles.actionsCell}>
                    {isIntegrated &&
                      promotion?.promotionCode &&
                      (row.kind === 'BottomBar' || row.kind === 'TopBar') && (
                        <TerciarySmallButton
                          onClick={() => setWidgetKindBeingEdited(row.kind)}
                          style={{ minWidth: 70 }}
                        >
                          Customize
                        </TerciarySmallButton>
                      )}
                  </Cell>
                </Row>
              ))
            )}
          </div>
        </Table>
        <div className={styles.cards}>
          <WidgetsCard
            title="Offer dialog"
            description="Customize the pop-up"
            type="dialog"
            onClick={() => setIsPopupDialogVisible(true)}
          />
          <WidgetsCard
            title="Campaign flow"
            description="Customize and see statistics"
            type="flow"
            to="/site-abandonment/campaign-flow"
          />
          <WidgetsCard
            title="Promotion"
            description={promotion?.promotionTagline}
            type="promotion"
            onClick={() => setIsPromotionEditDialogVisible(true)}
          />
        </div>

        {(isInternalAdmin || siteMap?.length > 0) && (
          <>
            <p className={styles.widgetCampaignsTitle}>Additional settings</p>
            <div className={styles.cards}>
              {isInternalAdmin && (
                <>
                  <WidgetCampaignCard
                    title="Consent request"
                    description="Will be sent to ask consent from guests using the widgets from their phones."
                    campaignName="OptInRequest"
                  />
                  <WidgetCampaignCard
                    title="Widget welcome"
                    description="Will be sent after guests gave express written consent."
                    campaignName="CardAbandonmentOptedIn"
                  />
                </>
              )}
              {siteMap?.length > 0 && (
                <WidgetCampaignCard
                  title="Excluded pages"
                  description="Hide the widgets on certain pages"
                  onClick={() => setIsExclusionDialogVisible(true)}
                />
              )}
              {isInternalAdmin && (
                <WidgetCampaignCard
                  title="Widget snippet"
                  description={
                    <>
                      {isOnline && (
                        <div className={styles.status}>
                          <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
                            <circle cx={8} cy={8} r={5} fill="#66BB6A" />
                          </svg>
                          Online
                        </div>
                      )}
                      {!isOnline && (
                        <div className={styles.status}>
                          <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
                            <circle cx={8} cy={8} r={5} fill="#FF7043" />
                          </svg>
                          Not found
                        </div>
                      )}
                    </>
                  }
                  onClick={() => setIsSnippetDialogVisible(true)}
                />
              )}
            </div>
          </>
        )}
      </div>
      {isSnippetDialogVisible && (
        <ConfirmationDialog
          onClose={() => setIsSnippetDialogVisible(false)}
          title="Widget snippet"
          theme="blacklist"
          maxWidth={600}
          hasConfirmButton={false}
        >
          <WidgetSnippet
            hasWrapper={false}
            isOnline={isOnline}
            hasTitle={false}
            onCheckClick={checkWidgetStatus}
            isChecking={isCheckingStatus}
          />
        </ConfirmationDialog>
      )}
      {isExclusionDialogVisible && (
        <ExcludedPagesDialog
          onClose={() => setIsExclusionDialogVisible(false)}
          siteMap={siteMap}
        />
      )}
      {isPopupDialogVisible && (
        <ConfigureWidgets onClose={() => setIsPopupDialogVisible(false)} />
      )}
    </div>
  )
}

export default Widgets
